import { CommonModule } from '@angular/common';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import {
  Component,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
} from '@angular/core';
import {
  distinctUntilChanged,
  finalize,
  Subscription,
} from 'rxjs';

import {
  CraditService,
  ProposedDisbursementRepaymentRowDto,
} from '../shared/services/cradit.service';
import { ToastService } from '../../../core/shared/services/toast.service';
import { PickListService } from '../../../core/shared/services/picklist.service';
import { PKL_GRP } from '../../../core/shared/models/picklist.model';

interface DisbursementRepaymentSummaryRow {
  rpymntTerm: number | null;
  month: string;
  proposedDisbursement: number;
  proposedRepayment: number;
  scheduledDisbursement: number;
  scheduledRepayment: number;
  maximumExposure: number;
}

/** One repayment term type (ref code group 13) and its own table of rows. */
interface DisbursementRepaymentSummaryGroup {
  rpymntTerm: number | null;
  termName: string;
  rows: DisbursementRepaymentSummaryRow[];
}

type SummaryAmountKey =
  | 'proposedDisbursement'
  | 'proposedRepayment'
  | 'scheduledDisbursement'
  | 'scheduledRepayment';

@Component({
  selector: 'app-disbursement-repayment-summary',
  standalone: true,
  imports: [CommonModule, TranslatePipe],
  templateUrl: './disbursement-repayment-summary.html',
  styleUrl: './disbursement-repayment-summary.css',
})
export class DisbursementRepaymentSummary
  implements OnInit, OnChanges, OnDestroy
{
  @Input() crdtPrpslSeq: number | null = null;
  @Input() sectionId = '';
  @Input() editable = false;

  rows: DisbursementRepaymentSummaryRow[] = [];

  /** Rows split into one table per repayment term type. */
  groups: DisbursementRepaymentSummaryGroup[] = [];

  /** Ref code group 13: `refCd` -> name (17 -> Conventional). */
  private termNames: Record<number, string> = {};

  loading = false;
  errorMessage = '';

  private loadedSequence: number | null = null;
  private initialized = false;

  private readonly subscriptions = new Subscription();

  constructor(
    private readonly creditService: CraditService,
    private readonly translate: TranslateService,
    private readonly toast: ToastService,
    private readonly pickListService: PickListService,
  ) {}

  ngOnInit(): void {
    this.initialized = true;

    this.loadTermNames();

    const sequenceSubscription =
      this.creditService.crdtPrpslSeq$
        .pipe(distinctUntilChanged())
        .subscribe((sequence) => {
          const normalizedSequence = Number(sequence ?? 0);

          if (
            Number.isFinite(normalizedSequence) &&
            normalizedSequence > 0
          ) {
            this.crdtPrpslSeq = normalizedSequence;
            this.loadSummary(normalizedSequence);
          }
        });

    this.subscriptions.add(sequenceSubscription);

    const saveTriggerSubscription =
      this.creditService.saveSectionTrigger$
        .subscribe((triggeredSectionId) => {
          // This tab only shows the saved plan — there is nothing to save
          // here, but the document builder waits for every triggered
          // section to report back, so confirm straight away.
          if (triggeredSectionId === this.sectionId) {
            this.toast.success('DISB_SUMMARY.SUMMARY_SAVED');

            this.creditService.reportSectionSaveResult({
              sectionId: this.sectionId,
              status: 'saved',
            });
          }
        });

    this.subscriptions.add(saveTriggerSubscription);

    const currentSequence = this.getProposalSequence();

    if (currentSequence > 0) {
      this.loadSummary(currentSequence);
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      !this.initialized ||
      !changes['crdtPrpslSeq']
    ) {
      return;
    }

    const sequence = this.getProposalSequence();

    if (sequence > 0) {
      this.loadSummary(sequence);
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  /** Column total for one table. */
  groupTotal(
    group: DisbursementRepaymentSummaryGroup,
    key: SummaryAmountKey,
  ): number {
    return this.roundAmount(
      group.rows.reduce(
        (total, row) => total + row[key],
        0,
      ),
    );
  }

  formatMonth(month: string): string {
    const normalizedMonth =
      this.normalizeMonth(month);

    if (!normalizedMonth) {
      return month || '-';
    }

    const [yearValue, monthValue] =
      normalizedMonth.split('-');

    const year = Number(yearValue);
    const monthIndex = Number(monthValue) - 1;

    if (
      !Number.isFinite(year) ||
      !Number.isFinite(monthIndex)
    ) {
      return month;
    }

    return new Intl.DateTimeFormat('en-GB', {
      month: 'short',
      year: '2-digit',
    }).format(new Date(year, monthIndex, 1));
  }

  trackByMonth(
    index: number,
    row: DisbursementRepaymentSummaryRow,
  ): string {
    return row.month || String(index);
  }

  private loadSummary(
    sequence: number,
    forceReload = false,
  ): void {
    if (
      !forceReload &&
      this.loadedSequence === sequence
    ) {
      return;
    }

    this.loading = true;
    this.errorMessage = '';

    const loadSubscription = this.creditService
      .getProposedDisbursementRepayment(sequence)
      .pipe(
        finalize(() => {
          this.loading = false;
        }),
      )
      .subscribe({
        next: (response) => {
          const summaryRows =
            this.extractSummaryRows(response);

          this.rows = this.buildRows(summaryRows);
          this.groups = this.buildGroups(this.rows);
          this.loadedSequence = sequence;

          // Unlike the old draft disbursement/repayment lists, every row
          // here (crdtPrpsdRpymntDsbmntSeq) is a persisted summary record —
          // its presence means this proposal's plan was already saved, so
          // "Save and Review Report" stays disabled and "Submit Credit
          // Proposal" stays visible from here on.
          if (summaryRows.length > 0) {
            this.creditService.setRepaymentPlanCalculated(
              sequence,
              true,
            );
          }
        },

        error: (error: any) => {
          console.error(
            'Disbursement repayment summary API error:',
            error,
          );

          this.errorMessage =
            error?.error?.message ??
            error?.error?.error ??
            this.translate.instant('DISB_SUMMARY.ERR_LOAD_SUMMARY');
        },
      });

    this.subscriptions.add(loadSubscription);
  }

  private buildRows(
    summaryRows: ProposedDisbursementRepaymentRowDto[],
  ): DisbursementRepaymentSummaryRow[] {
    return summaryRows
      .map((item): DisbursementRepaymentSummaryRow => ({
        rpymntTerm:
          item.rpymntTerm != null ? Number(item.rpymntTerm) : null,
        month: this.normalizeMonth(item.schMth),
        proposedDisbursement: this.toAmount(item.prpsdDsbmnt),
        proposedRepayment: this.toAmount(item.prpsdRpymnt),
        scheduledDisbursement: this.toAmount(item.schDsbmnt),
        scheduledRepayment: this.toAmount(item.schRpymnt),
        maximumExposure: this.toAmount(item.maxExpsr),
      }))
      .filter((row) => !!row.month)
      .sort((first, second) =>
        first.month.localeCompare(second.month),
      );
  }

  /** One group per repayment term, in the order the terms first appear. */
  private buildGroups(
    rows: DisbursementRepaymentSummaryRow[],
  ): DisbursementRepaymentSummaryGroup[] {
    const groups: DisbursementRepaymentSummaryGroup[] = [];

    for (const row of rows) {
      let group = groups.find(
        (item) => item.rpymntTerm === row.rpymntTerm,
      );

      if (!group) {
        group = {
          rpymntTerm: row.rpymntTerm,
          termName: this.termName(row.rpymntTerm),
          rows: [],
        };
        groups.push(group);
      }

      group.rows.push(row);
    }

    return groups;
  }

  private termName(rpymntTerm: number | null): string {
    if (rpymntTerm == null) {
      return '-';
    }

    return this.termNames[rpymntTerm] ?? String(rpymntTerm);
  }

  /** Repayment term names from ref code group 13, keyed by `refCd`. */
  private loadTermNames(): void {
    const termSubscription = this.pickListService
      .loadCodesAsync([PKL_GRP.FACILITY_TYPE])
      .subscribe({
        next: () => {
          this.termNames = {};

          for (const option of this.pickListService.getOptionsSync(
            PKL_GRP.FACILITY_TYPE,
          )) {
            const refCd = Number(option.original?.refCd);

            if (Number.isFinite(refCd)) {
              this.termNames[refCd] = String(option.label ?? '');
            }
          }

          // Groups may have been built before the names arrived.
          this.groups = this.groups.map((group) => ({
            ...group,
            termName: this.termName(group.rpymntTerm),
          }));
        },
        error: (error: unknown) => {
          console.error('Unable to load repayment terms:', error);
        },
      });

    this.subscriptions.add(termSubscription);
  }

  private extractSummaryRows(
    response: any,
  ): ProposedDisbursementRepaymentRowDto[] {
    const source =
      response?.data ?? response;

    const list = Array.isArray(source)
      ? source
      : [];

    return list.filter(
      (item: any) =>
        Number(item?.delFlg ?? 0) !== 1,
    );
  }

  private normalizeMonth(
    value: unknown,
  ): string {
    const dateValue = String(
      value ?? '',
    ).slice(0, 10);

    const match =
      /^(\d{4})-(\d{2})-\d{2}$/.exec(
        dateValue,
      );

    if (!match) {
      return '';
    }

    return `${match[1]}-${match[2]}-01`;
  }

  private getProposalSequence(): number {
    const sequence = Number(
      this.crdtPrpslSeq ??
        this.creditService.getCrdtPrpslSeq() ??
        0,
    );

    return Number.isFinite(sequence) &&
      sequence > 0
      ? sequence
      : 0;
  }

  private toAmount(value: unknown): number {
    const amount = Number(value ?? 0);

    if (
      !Number.isFinite(amount) ||
      amount < 0
    ) {
      return 0;
    }

    return this.roundAmount(amount);
  }

  private roundAmount(value: number): number {
    return (
      Math.round(
        (Number(value) + Number.EPSILON) *
          100,
      ) / 100
    );
  }
}